import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCompanies, toggleCompanyVerification } from '../../Redux/adminSlice';
import ContentLoader from '../utils/ContentLoader';
import { FiSearch, FiRefreshCw, FiEye, FiCheck, FiX, FiExternalLink } from 'react-icons/fi';

const CompaniesManagement = () => {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view'); // 'view' or 'edit'

  const dispatch = useDispatch();
  const { data, loading, error, pagination } = useSelector((state) => state.admin.companies);

  useEffect(() => {
    dispatch(fetchCompanies({ page: currentPage, limit: 10, search }));
  }, [dispatch, currentPage, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(fetchCompanies({ page: 1, limit: 10, search }));
  };

  const handleRefresh = () => {
    dispatch(fetchCompanies({ page: currentPage, limit: 10, search }));
  };

  const handleVerificationToggle = (companyId, isVerified) => {
    dispatch(toggleCompanyVerification({ companyId, isVerified }))
      .unwrap()
      .then(() => {
        if (selectedCompany && selectedCompany._id === companyId) {
          setSelectedCompany({ ...selectedCompany, isVerified });
        }
      })
      .catch((err) => console.error('Failed to update verification status:', err));
  };

  const openCompanyModal = (company, type = 'view') => {
    setSelectedCompany(company);
    setModalType(type);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCompany(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Company Management</h1>
        <div className="flex space-x-2">
          <form className="relative" onSubmit={handleSearch}>
            <input
              type="text"
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 w-full md:w-64"
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <button
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-orange-500 hover:text-orange-600"
            >
              Search
            </button>
          </form>
          <button
            onClick={handleRefresh}
            className="p-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
            title="Refresh"
          >
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Show loader when loading (initial load or refresh) */}
      {loading && <ContentLoader message="Loading companies..." />}

      {/* Error UI */}
      {!loading && error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-md">
          <h3 className="text-lg font-medium">Error loading companies</h3>
          <p>{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verified
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No companies found
                  </td>
                </tr>
              ) : (
                data.map((company) => (
                  <tr key={company._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-medium mr-3">
                          {company.companyName?.charAt(0).toUpperCase() || 'C'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{company.companyName}</div>
                          <div className="text-xs text-gray-500 flex items-center">
                            <a
                              href={company.companyWebsite}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              Website <FiExternalLink className="ml-1 w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.firstName} {company.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {company.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {company.isVerified ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <FiCheck className="w-3 h-3 mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <FiX className="w-3 h-3 mr-1" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(company.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 space-x-2">
                      <button
                        onClick={() => openCompanyModal(company, 'view')}
                        className="text-orange-500 hover:text-orange-600"
                        title="View Details"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleVerificationToggle(company._id, !company.isVerified)}
                        className={`ml-2 ${
                          company.isVerified
                            ? 'text-red-600 hover:text-red-900'
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={company.isVerified ? 'Remove Verification' : 'Verify Company'}
                      >
                        {company.isVerified ? <FiX className="w-5 h-5" /> : <FiCheck className="w-5 h-5" />}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between items-center">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> companies
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                  disabled={currentPage === pagination.pages}
                  className={`px-4 py-2 text-sm rounded-md ${
                    currentPage === pagination.pages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Company Details Modal */}
      {showModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-medium text-gray-900">
                {modalType === 'view' ? 'Company Details' : 'Edit Company'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3">
                  <div className="flex flex-col items-center mb-6">
                    <div className="h-20 w-20 rounded-full bg-orange-500 flex items-center justify-center text-white text-2xl font-bold mb-3">
                      {selectedCompany.companyName?.charAt(0).toUpperCase() || 'C'}
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 text-center">{selectedCompany.companyName}</h3>
                    <p className="text-sm text-gray-500">{selectedCompany.email}</p>
                    
                    <a
                      href={selectedCompany.companyWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center mt-2"
                    >
                      Visit Website <FiExternalLink className="ml-1 w-4 h-4" />
                    </a>
                    
                    <div className="mt-3">
                      {selectedCompany.isVerified ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          <FiCheck className="w-4 h-4 mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                          <FiX className="w-4 h-4 mr-1" />
                          Not Verified
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-3">VERIFICATION ACTION</h4>
                    <button
                      onClick={() => {
                        handleVerificationToggle(selectedCompany._id, !selectedCompany.isVerified);
                      }}
                      className={`w-full py-2 px-4 rounded-md text-white ${
                        selectedCompany.isVerified
                          ? 'bg-red-500 hover:bg-red-600'
                          : 'bg-green-500 hover:bg-green-600'
                      }`}
                    >
                      {selectedCompany.isVerified ? 'Remove Verification' : 'Verify Company'}
                    </button>
                  </div>
                </div>
                
                <div className="md:w-2/3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-xs text-gray-500">First Name</p>
                      <p className="text-sm font-medium">{selectedCompany.firstName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Last Name</p>
                      <p className="text-sm font-medium">{selectedCompany.lastName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium">{selectedCompany.email}</p>
                    </div>
                  </div>
                  
                  <h4 className="text-sm font-medium text-gray-500 uppercase mb-3">Account Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-xs text-gray-500">Last Login</p>
                      <p className="text-sm font-medium">
                        {selectedCompany.lastLogin
                          ? new Date(selectedCompany.lastLogin).toLocaleString()
                          : 'Never'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Joined On</p>
                      <p className="text-sm font-medium">
                        {new Date(selectedCompany.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Account Status</p>
                      <p className="text-sm font-medium">
                        {selectedCompany.isVerified ? 'Verified' : 'Pending Verification'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesManagement; 